import React, { useState, useEffect, useCallback } from "react";
import { useNavigate  } from "react-router-dom";
import { ArrowUp, ArrowDown } from "lucide-react";

const AdminList = () => {
    const navigate = useNavigate();
    const [admins, setAdmins] = useState([]);
    const [loading, setLoading] = useState(true);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [error, setError] = useState(null);
    const [sortConfig, setSortConfig] = useState({
        key: "email",
        direction: "ascending"
    });

    const fetchAdmins = useCallback(async () => {
        try {
            const token = localStorage.getItem("token");
            if (!token) {
                navigate("/");
                return;
            }

            const response = await fetch("/api/getAllAdmins", {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                throw new Error("Failed to fetch admins");
            }

            const data = await response.json();
            setAdmins(data);
            setLoading(false);
        } catch (err) {
            setError(err.message);
            setLoading(false);
        }
    }, [navigate]);

    useEffect(() => {
        fetchAdmins();
    }, [fetchAdmins]);


    const handleDelete = async (email) => {
        if (!window.confirm(`Are you sure you want to delete admin:  ${email}?`)) {
            return;
        }

        setDeleteLoading(true);
        setError(null);

        try {
            const token = localStorage.getItem("token");
            if (!token) {
                navigate("/")
                return;
            }

            const response = await fetch(`/api/admin/${email}`, {
                method: "DELETE",
                headers: {
                    Authorization: `Bearer ${token}`
                },
            });

            if (!response.ok) {
                throw new Error (await response.text());
            }

            await fetchAdmins();
        } catch (err) {
            setError(err.message);
        } finally {
            setDeleteLoading(false);
        }
    };

    const handleSort = (key) => {
        let direction = "ascending";
        if (sortConfig.key === key && sortConfig.direction === "ascending") {
            direction = "descending";
        }
        setSortConfig({ key, direction })
    };

    const sortedAdmins = [...admins].sort((a,b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
            return sortConfig.direction === "ascending" ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
            return sortConfig.direction === "ascending" ? 1: -1;
        }
        return 0;
    });

    if (loading) return <div className="p-8 text-center">Loading...</div>;
    if (error) return <div className="p-8 text-center text-red-500">Error: {error}</div>;

    return (
        <div className="min-h-screen py-8" style={{ background: 'linear-gradient(to bottom, #b3e672 10%, #7ba02d 90%)' }}>
            <div className="max-w-4xl mx-auto px-4">
                <div className="bg-white rounded-lg shadow-lg p-8">
                    <div className="flex justify-between items-center mb-6">
                        <h2 className="text-2xl font-bold">Admin Management</h2>
                        <div className="space-x-4">
                            <button
                                onClick={() => navigate("/dashboard")}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                            >
                                ← Back to Dashboard
                            </button>
                            <button
                                onClick={() => navigate("/add-admin")}
                                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                            >
                                Add New Admin
                            </button>
                        </div>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th
                                        onClick={() => handleSort("email")}
                                        className="cursor-pointer px-4 py-2 text-gray-600 hover:bg-gray-200 w-fit font-semibold select-none rounded"
                                        >
                                        <div className="flex flex-col items-start">
                                            <span className="ml-2">Email</span>
                                            <div className="flex justify-center ml-2">
                                                <ArrowUp
                                                    className={`w-4 h-4 ${
                                                        sortConfig.key === "email" &&
                                                        sortConfig.direction === "ascending"
                                                            ? "text-blue-500"
                                                            : "text-gray-400"
                                                    }`}
                                                />
                                                <ArrowDown
                                                    className={`w-4 h-4 ${
                                                        sortConfig.key === "email" &&
                                                        sortConfig.direction === "descending"
                                                            ? "text-blue-500"
                                                            : "text-gray-400"
                                                    }`}
                                                />
                                            </div>
                                        </div>
                                    </th>
                                    <th className="px-6 py-3 text-center text-gray-600 font-semibold">
                                        Privilege
                                    </th>
                                    <th className="px-6 py-3 text-gray-600 font-semibold text-right">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {sortedAdmins.map((admin) => (
                                    <tr key={admin.email}>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm text-gray-900">{admin.email}</div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm text-gray-900 text-center" >{admin.privilege}</div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right">
                                            <button
                                                onClick={() => handleDelete(admin.email)}
                                                disabled={deleteLoading}
                                                className="text-red-600 hover:text-red-900"
                                            >
                                                {deleteLoading ? 'Deleting...' : 'Delete'}
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminList;